import { useCallback, useEffect, useMemo, useState } from 'react'
import { resolveNoteAuthorName } from '@/lib/utils/noteFormatting'
import { fetchNotes } from '../services/residentApi'
import type { PublicNoteRow, PublicNotesMessageParent, ResidentNote } from '../types'
import { getPublicNoteContent, getPublicNoteEmailSendTo } from '../utils/residentPublicNotesHelpers'

function toNoteArray(value: unknown): ResidentNote[] {
  if (!value) return []
  if (Array.isArray(value)) return value as ResidentNote[]
  if (typeof value === 'object') return Object.values(value as Record<string, ResidentNote>)
  return []
}

/**
 * Prefer a non-empty `message` / `messages` list (Angular uses `residentDetail.message`).
 * An empty `messages: []` must not hide a populated `message` array.
 */
function normalizeDetailMessages(detail: PublicNotesMessageParent): ResidentNote[] {
  if (!detail) return []
  const raw = detail as Record<string, unknown>
  const fromMessages = toNoteArray(raw.messages)
  if (fromMessages.length) return fromMessages
  return toNoteArray(raw.message)
}

function parseSortTime(value: unknown): number {
  if (value === undefined || value === null || value === '') return 0
  if (typeof value === 'number') return value > 1e12 ? value : value * 1000
  const n = Number(value)
  if (!Number.isNaN(n) && String(value).trim().length <= 10) return n * 1000
  const d = Date.parse(String(value))
  return Number.isNaN(d) ? 0 : d
}

function withResolvedAuthorName(row: PublicNoteRow): PublicNoteRow {
  const resolved = resolveNoteAuthorName(row)
  if (resolved !== '—' && !String(row.name ?? '').trim()) {
    row.name = resolved
  }
  return row
}

function normalizePublicNoteRow(row: PublicNoteRow, type: 'notes' | 'message'): PublicNoteRow {
  row.type = type
  row.content = getPublicNoteContent(row)
  const emailSendTo = getPublicNoteEmailSendTo(row)
  if (emailSendTo) row.email_send_to = emailSendTo
  return withResolvedAuthorName(row)
}

/**
 * Default-tab public notes: GET `/api/notes/:id?notes_for=DEFAULT` merged with
 * `residentDetail.message` / `messages` (Angular `processDefaultNotesandMessage`).
 */
export function usePublicNotesAndMessages(
  defaultId: string | number | undefined,
  residentDetail: PublicNotesMessageParent,
) {
  const [defaultNotes, setDefaultNotes] = useState<unknown[]>([])
  const [loading, setLoading] = useState(false)

  const loadDefaultNotes = useCallback(async (options?: { silent?: boolean }) => {
    if (!defaultId) return
    if (!options?.silent) setLoading(true)
    try {
      const response = await fetchNotes(defaultId, { notes_for: 'DEFAULT' })
      const arr = Array.isArray(response)
        ? response
        : response && typeof response === 'object' && 'data' in (response as object)
          ? (response as { data: unknown[] }).data
          : []
      setDefaultNotes(Array.isArray(arr) ? arr : [])
    } finally {
      if (!options?.silent) setLoading(false)
    }
  }, [defaultId])

  useEffect(() => {
    void loadDefaultNotes()
    const id = window.setInterval(() => {
      void loadDefaultNotes({ silent: true })
    }, 30_000)
    return () => window.clearInterval(id)
  }, [loadDefaultNotes])

  const mergedRows = useMemo(() => {
    const rows: PublicNoteRow[] = []
    for (const n of defaultNotes) {
      const row =
        typeof n === 'object' && n !== null ? ({ ...(n as object) } as PublicNoteRow) : ({} as PublicNoteRow)
      rows.push(normalizePublicNoteRow(row, 'notes'))
    }
    for (const m of normalizeDetailMessages(residentDetail)) {
      const row = { ...(m as object) } as PublicNoteRow
      rows.push(normalizePublicNoteRow(row, row.type === 'notes' ? 'notes' : 'message'))
    }
    rows.sort((a, b) => parseSortTime(b.created_at) - parseSortTime(a.created_at))
    return rows
  }, [defaultNotes, residentDetail])

  return { mergedRows, loading, reload: loadDefaultNotes }
}

export type { PublicNoteRow } from '../types/hooks'
